import { ComponentProps, ReactElement, ReactNode, useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { Dialog } from '@headlessui/react';
import { AnimatePresence, motion } from 'framer-motion';
import styled from 'styled-components';

import { Button, ButtonProps } from '@components/button';
import { CancelSaveButtons, CancelSaveButtonsProps } from '@components/cancel-save-buttons';
import { useDeepEqualMemoValue } from '@components/component-helpers';
import { IconProps } from '@components/icons';
import { IconCloseRegular } from '@components/icons/close-regular';
import { RelativeSpinner } from '@components/loading-spinner';
import { HEADLESS_PORTAL_ID } from '@components/menu';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useMinimumDelay } from '@hooks/use-minimum-delay';
import { useTranslate } from '@locale/translate';
import { createCountStore } from '@stores/create-count-store';
import { Color, theme } from '@styles/theme';
import { noop } from '@utils/browser';
import { pxToRem } from '@utils/converters';
import { second } from '@utils/milliseconds';
import { MakePropertiesOptional, Transient } from '@utils/types';

export type ModalOptions = {
  variant?: ModalVariant;
  backgroundColor?: Color | 'gradient';
  hasBlurredAndForcedBackdrop?: boolean;
  onHide?(): void;
  onBeforeChange?(): boolean | void;
};

export const modalStore = createCountStore();

const handleHide = (forceHide: boolean) => {
  if (confirmationRegistry) {
    confirmationRegistry.options?.onHide?.();
    confirmationRegistry = null;
    confirmationModalStore.reset();
    if (!forceHide) {
      return;
    }
  }
  if (modalRegistry?.options) {
    const { onBeforeChange, onHide } = modalRegistry.options;
    const shouldContinue = forceHide || (onBeforeChange?.() ?? true);
    if (shouldContinue) {
      onHide?.();
      modalRegistry = null;
      modalStore.reset();
    }
  }
};

export const hideModal = () => handleHide(false);

export const forceHideModal = () => handleHide(true);

type ModalComponent = (props: any) => ReactElement;

type ModalRegistry = {
  options?: ModalOptions;
  ModalComponent(props: object): ReactElement;
};

let modalRegistry: ModalRegistry | null = null;
let modalInstanceId = 0;

const confirmationModalStore = createCountStore();

let confirmationRegistry: ModalRegistry | null = null;

export type ModalVariant =
  | 'SMALL'
  | 'REGULAR'
  | 'LARGE'
  | 'EXTRA_LARGE'
  | 'EXTRA_EXTRA_LARGE'
  | 'FULLSCREEN';

type ModalSizeProps = {
  width: string;
  maxWidth?: string;
  maxHeight: string;
  height: string;
};

type ModalSizes = {
  [key in ModalVariant]: ModalSizeProps;
};

// The cap every non-fullscreen modal is held to. Exported so content sized
// against it does not have to hard-code the number.
export const MODAL_MAX_HEIGHT = '90vh';

const sizes: ModalSizes = {
  // The width a sidepanel and the designs' single-field modals share.
  SMALL: {
    width: pxToRem(424),
    maxHeight: '90vh',
    height: 'inherit',
  },
  REGULAR: {
    width: pxToRem(504),
    maxHeight: MODAL_MAX_HEIGHT,
    height: 'inherit',
  },
  LARGE: {
    width: pxToRem(728),
    maxHeight: MODAL_MAX_HEIGHT,
    height: 'inherit',
  },
  EXTRA_LARGE: {
    width: '88vw',
    maxWidth: pxToRem(960),
    maxHeight: '72vh',
    height: '72vh',
  },
  EXTRA_EXTRA_LARGE: {
    width: pxToRem(1200),
    maxWidth: pxToRem(1200),
    maxHeight: pxToRem(568),
    height: pxToRem(568),
  },
  FULLSCREEN: {
    width: '94vw',
    maxHeight: MODAL_MAX_HEIGHT,
    height: MODAL_MAX_HEIGHT,
  },
};

const TRANSITION_DURATION = 0.3;
const BACKDROP_OPACITY = 0.5;

export const useModal = <T extends ModalComponent>(component: T, options?: ModalOptions) => {
  const opts = useDeepEqualMemoValue(options);
  return useCallback(
    <K extends ComponentProps<T>>(props?: K, optionsOnShow?: ModalOptions) => {
      return showModal({
        component,
        options: { ...opts, ...optionsOnShow },
        props,
      });
    },
    [component, opts],
  );
};

export const showModal = <T extends ModalComponent, K extends ComponentProps<T>>({
  component: ModalComponent,
  options,
  props,
}: {
  component: T;
  options?: ModalOptions;
  props?: K;
}) => {
  modalInstanceId++;
  modalRegistry = { options: { ...options, ...props }, ModalComponent };
  modalStore.increase();
};

export const useIsModalActive = () => {
  const { useStore } = modalStore;
  const isActive = useStore((s) => s.count > 0);

  return isActive;
};

const showConfirmationModal = <T extends ModalComponent, K extends ComponentProps<T>>({
  component: ModalComponent,
  options,
  props,
}: {
  component: T;
  options?: ModalOptions;
  props?: K;
}) => {
  confirmationRegistry = { options: { ...options, ...props }, ModalComponent };
  confirmationModalStore.increase();
};

export const useConfirmationModal = <T extends ModalComponent>(
  component: T,
  options?: ModalOptions,
) => {
  const opts = useDeepEqualMemoValue(options);
  return useCallback(
    <K extends ComponentProps<T>>(props?: K) => {
      showConfirmationModal({ component, options: opts, props });
    },
    [component, opts],
  );
};

export const useModalEventHandlers = () => {
  const setOnBeforeChange = (onBeforeChange: ModalOptions['onBeforeChange']) => {
    if (modalRegistry) {
      modalRegistry.options = { ...modalRegistry.options, onBeforeChange };
    }
  };

  return { setOnBeforeChange };
};

type ModalProps = React.HTMLAttributes<HTMLDivElement> & {
  isLoading?: boolean;
  children?: ReactNode;
};

const MIN_LOADING_DISPLAY_MS = 500;

export const Modal = ({ isLoading = false, children, ...props }: ModalProps) => {
  const showLoading = useMinimumDelay(isLoading, MIN_LOADING_DISPLAY_MS);

  return (
    <ModalWrapper {...props}>
      {children}
      <AnimatePresence>
        {showLoading && (
          <LoadingOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
          >
            <RelativeSpinner />
          </LoadingOverlay>
        )}
      </AnimatePresence>
    </ModalWrapper>
  );
};

const ModalWrapper = styled.div({
  display: 'contents',
});

const LoadingOverlay = styled(motion.div)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: theme.color.backgroundLightDark,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: pxToRem(16),
  zIndex: 10,
});

const ModalProvider = () => {
  const { useStore } = modalStore;
  const count = useStore((s) => s.count);
  const isActive = count > 0;
  // isDialogOpen is used to allow the hide animation to complete before the dialog is closed
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const location = useLocation();
  const { options, ModalComponent } = modalRegistry ?? {};
  const {
    onHide,
    variant = 'REGULAR',
    backgroundColor,
    hasBlurredAndForcedBackdrop,
  } = options ?? {};

  useEffect(() => {
    forceHideModal();
  }, [location.pathname]);

  useEffect(() => {
    if (isActive) {
      setIsDialogOpen(true);
    } else {
      const timer = setTimeout(() => setIsDialogOpen(false), TRANSITION_DURATION * second);
      return () => clearTimeout(timer);
    }
  }, [isActive]);

  useEffect(() => {
    if (!isActive && !isDialogOpen) {
      onHide?.();
      modalRegistry = null;
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, isDialogOpen]);

  return (
    <Dialog
      id={HEADLESS_PORTAL_ID}
      as="div"
      onClose={hasBlurredAndForcedBackdrop ? noop : hideModal}
      open={isDialogOpen}
    >
      <StyledContainer>
        <AnimatePresence>
          {isActive && (
            <>
              <Backdrop
                initial={{ opacity: 0 }}
                animate={{ opacity: hasBlurredAndForcedBackdrop ? 1 : BACKDROP_OPACITY }}
                transition={{ duration: TRANSITION_DURATION }}
                exit={{ opacity: 0 }}
                $hasBlurredAndForcedBackdrop={hasBlurredAndForcedBackdrop}
              />
              <Dialog.Panel>
                <Container
                  key={modalInstanceId}
                  className={backgroundColor === 'gradient' ? 'dark' : undefined}
                  $variant={variant}
                  $backgroundColor={backgroundColor}
                  id="modal"
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: TRANSITION_DURATION, ease: 'easeOut' }}
                >
                  {ModalComponent && <ModalComponent {...options} />}
                </Container>
              </Dialog.Panel>
            </>
          )}
        </AnimatePresence>
        <ConfirmationLayer />
      </StyledContainer>
    </Dialog>
  );
};

// Its own nested Dialog, not a second Dialog.Panel — else headlessui reads a click on the confirmation as an outside-click on the host modal and re-opens it.
const ConfirmationLayer = () => {
  const isActive = confirmationModalStore.useStore((state) => state.count > 0);
  const { options, ModalComponent } = confirmationRegistry ?? {};

  return (
    <Dialog as="div" onClose={hideModal} open={isActive}>
      {isActive && ModalComponent && (
        <ConfirmationContainer data-testid="confirmation-modal">
          <Backdrop
            initial={{ opacity: 0 }}
            animate={{ opacity: BACKDROP_OPACITY }}
            transition={{ duration: TRANSITION_DURATION }}
            exit={{ opacity: 0 }}
          />
          <Dialog.Panel>
            <Container
              $variant="REGULAR"
              $backgroundColor={undefined}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: TRANSITION_DURATION, ease: 'easeOut' }}
            >
              <ModalComponent {...options} />
            </Container>
          </Dialog.Panel>
        </ConfirmationContainer>
      )}
    </Dialog>
  );
};

const ConfirmationContainer = styled.div({
  position: 'fixed',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000000, // above a host modal's own full-screen overlays (e.g. the code editor's loader at 999999)
});

const StyledContainer = styled(motion.div)({
  position: 'fixed',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 9999,
});

const Backdrop = styled(motion.div)<Transient<ModalOptions, 'hasBlurredAndForcedBackdrop'>>(
  ({ $hasBlurredAndForcedBackdrop }) => ({
    position: 'fixed',
    backgroundColor: theme.color.backdropOverlay,
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    overflow: 'hidden',
    backdropFilter: $hasBlurredAndForcedBackdrop ? `blur(${pxToRem(8)})` : undefined,
    zIndex: -1,
  }),
);

const CONTENT_PADDING = pxToRem(40);
const HEADER_FOOTER_PADDING = pxToRem(24);
// Shared so the header and the footer cannot drift out of step again.
const HEADER_FOOTER_PADDING_Y = pxToRem(16);
// The footer's buttons make it a control height tall; the header has to be
// floored to match, since a header holding only a title has nothing that tall in
// it. Border-box, so the floor includes the padding and the rule.
export const HEADER_FOOTER_HEIGHT = `calc(${
  theme.base.height
} + ${HEADER_FOOTER_PADDING_Y} * 2 + ${pxToRem(1)})`;

const Container = styled(motion.div)<{
  $variant: ModalOptions['variant'];
  $backgroundColor: ModalOptions['backgroundColor'];
}>(({ $variant = 'REGULAR', $backgroundColor }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column' as 'column',
  overflow: 'hidden',
  width: sizes[$variant].width,
  maxWidth: sizes[$variant].maxWidth,
  height: sizes[$variant].height,
  maxHeight: sizes[$variant].maxHeight,
  ...($backgroundColor === 'gradient'
    ? {
        backgroundColor: theme.color.purple400,
        backgroundImage: `radial-gradient(ellipse 120% 100% at 100% 50%, ${theme.color.violet100} -64%, ${theme.color.tealTrans10} 70%)`,
      }
    : {
        backgroundColor: $backgroundColor
          ? theme.color[$backgroundColor]
          : theme.color.backgroundLightDark,
      }),
  borderRadius: pxToRem(16),
  boxShadow: theme.shadow.fancy,
  // A form between the modal and its sections stops the height cap above from
  // reaching `Modal.Content`, so its `overflow: auto` has nothing to scroll
  // within and the footer is pushed past the bottom instead. Not `> form`:
  // `ModalWrapper` is `display: contents`, so the form is a flex item here but
  // still a grandchild in the DOM a selector matches against.
  [`& form:has(> * > .${MODAL_CONTENT_CLASSNAME})`]: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minHeight: 0,
  },
  [`& form > *:has(> .${MODAL_CONTENT_CLASSNAME})`]: {
    flexGrow: 1,
    minHeight: 0,
  },
}));

export const MODAL_CONTENT_CLASSNAME = 'modal-content';

type ModalContentProps = {
  // For a modal with no header or footer, where nothing else supplies the vertical inset.
  hasUniformPadding?: boolean;
};

const ModalContent = styled.div.attrs({
  className: MODAL_CONTENT_CLASSNAME,
})<ModalContentProps>(({ hasUniformPadding = false }) => ({
  overflow: 'auto',
  padding: hasUniformPadding ? CONTENT_PADDING : `${pxToRem(24)} ${CONTENT_PADDING}`,
  minHeight: 0,
  flexGrow: 1,
  font: theme.font.regular16,
  color: theme.color.textPrimary,
}));

export const MODAL_HEADER_CLASSNAME = 'modal-header';

type ModalHeaderProps = React.HTMLAttributes<HTMLDivElement> & {
  // Pass false when whatever sits directly below the header brings its own rule
  // (a tab bar, for instance) - two stacked dividers box the element in.
  hasDivider?: boolean;
};

const StyledModalHeaderDiv = styled.div({
  // Column, not row: two headers stack a title above a second element, and a row
  // would put them side by side.
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  minHeight: HEADER_FOOTER_HEIGHT,
  padding: `${HEADER_FOOTER_PADDING_Y} ${HEADER_FOOTER_PADDING}`,
  marginBottom: 0,
  borderBottom: `${pxToRem(1)} solid ${theme.color.border10}`,
  font: theme.font.bold18,
  color: theme.color.textPrimary,

  "&[data-has-divider='false']": {
    borderBottom: 'none',
  },
});

// This is a plain layout wrapper — it must NOT register itself as
// `Dialog.Title` (HeadlessUI). A header commonly also holds buttons (close,
// manage, etc.), and if the header itself were `Dialog.Title`, all of that
// button text would become part of the dialog's accessible name too.
// Only the title text — via `HeaderTitle`/`Modal.HeaderTitle` below — carries
// `Dialog.Title` now.
const ModalHeader = ({ hasDivider = true, ...props }: ModalHeaderProps) => (
  <StyledModalHeaderDiv data-has-divider={hasDivider} {...props} />
);

export const MODAL_FOOTER_CLASSNAME = 'modal-footer';

const ModalFooter = styled.div.attrs({
  className: MODAL_FOOTER_CLASSNAME,
})({
  marginTop: 0,
  padding: `${HEADER_FOOTER_PADDING_Y} ${HEADER_FOOTER_PADDING}`,
  borderTop: `${pxToRem(1)} solid ${theme.color.border10}`,
});

export type SimpleHeaderProps = {
  title: string;
  color?: Color;
  icon?: ReactElement<IconProps>;
  skipCloseButton?: boolean;
  closeButtonProps?: ButtonProps;
};

const SimpleHeader = ({
  title,
  icon,
  skipCloseButton = false,
  closeButtonProps,
  ...rest
}: SimpleHeaderProps) => (
  <StyledTitleHeader {...rest}>
    {icon ? <SimpleIconHeader icon={icon} title={title} /> : <HeaderTitle title={title} />}
    {!skipCloseButton && <CloseButton {...closeButtonProps} />}
  </StyledTitleHeader>
);

const StyledTitleHeader = styled(ModalHeader)({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
});

export const CloseButton = (props: ButtonProps) => (
  <Button iconLeft={<IconCloseRegular />} variant="ghost" onClick={hideModal} {...props} />
);

// `Dialog.Title` (HeadlessUI) is what registers the dialog's title id and
// wires `aria-labelledby` onto the `Dialog`. It has to wrap only the title
// text, not the whole header — a header commonly also holds buttons (close,
// manage, etc.), and those would otherwise become part of the dialog's
// accessible name too. Rendering `Text` through `Dialog.Title`'s own `as`
// prop keeps the title's existing typography while registering only the
// title text as the accessible name.
const HeaderTitle = ({ title, color }: Pick<SimpleHeaderProps, 'title' | 'color'>) => (
  <Dialog.Title as={Text} text={title} color={color} font="bold18" />
);

const SimpleIconHeader = ({
  title,
  color,
  icon,
}: Pick<SimpleHeaderProps, 'title' | 'color' | 'icon'>) => (
  <Stack space={2} orientation="horizontal" align="center">
    {icon}
    <HeaderTitle color={color} title={title} />
  </Stack>
);

type SimpleFooterProps = MakePropertiesOptional<
  Omit<CancelSaveButtonsProps, 'alwaysShow'>,
  'onCancel'
>;

const SimpleFooter = ({ onCancel, onSuccess, ...rest }: SimpleFooterProps) => (
  <ModalFooter>
    <CancelSaveButtons
      {...rest}
      alwaysShow
      onCancel={() => {
        onCancel?.();
        hideModal();
      }}
      onSuccess={() => {
        onSuccess?.();
        hideModal();
      }}
    />
  </ModalFooter>
);

const TruncatedTitle = styled(Stack)({
  minWidth: 0,
  overflow: 'hidden',
  '& > *': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});

const FullscreenHeader = ({ title, actions }: { title: string; actions?: JSX.Element }) => {
  const { t } = useTranslate();
  return (
    <StyledFullscreenHeader>
      <TruncatedTitle orientation="horizontal" align="center">
        <HeaderTitle title={title} />
      </TruncatedTitle>
      <Stack orientation="horizontal" align="center" space={6}>
        <HeaderActions id="header-actions">{actions}</HeaderActions>
        <Button
          aria-label={t('core.close')}
          iconLeft={<IconCloseRegular />}
          variant="secondary"
          onClick={hideModal}
        />
      </Stack>
    </StyledFullscreenHeader>
  );
};

const StyledFullscreenHeader = styled(ModalHeader)({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  backgroundColor: theme.color.backgroundLightDark,
});

export const HeaderActions = styled.div({
  flexGrow: 1,
  display: 'flex',
  justifyContent: 'flex-end',
  position: 'relative',
});

const FullscreenContent = ({
  children,
  ...props
}: Omit<React.HTMLAttributes<HTMLDivElement>, 'className'>) => (
  <StyledFullscreenContent {...props}>
    <FullscreenContentCentred>{children}</FullscreenContentCentred>
  </StyledFullscreenContent>
);

const StyledFullscreenContent = styled.div.attrs({
  className: MODAL_CONTENT_CLASSNAME,
})({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  minHeight: 0,
  overflow: 'auto',
});

const FullscreenContentCentred = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  marginBlock: 'auto',
});

const FullscreenFooter = styled.div.attrs({
  className: MODAL_FOOTER_CLASSNAME,
})({
  padding: `${HEADER_FOOTER_PADDING_Y} ${CONTENT_PADDING}`,
  borderTop: `${pxToRem(1)} solid ${theme.color.border10}`,
});

Modal.Provider = ModalProvider;
Modal.SimpleHeader = SimpleHeader;
Modal.SimpleFooter = SimpleFooter;
Modal.HeaderTitle = HeaderTitle;
Modal.Header = ModalHeader;
Modal.Content = ModalContent;
Modal.Footer = ModalFooter;
Modal.FullscreenHeader = FullscreenHeader;
Modal.FullscreenContent = FullscreenContent;
Modal.FullscreenFooter = FullscreenFooter;
